import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useMemo } from "react";

import { type WorkerRequirement } from "./api/types";
import { useGetWorkerRequirements } from "./api/useGetWorkerRequirements";
import { useCredentialRequirementConfiguration } from "./useCredentialRequirementConfiguration";

export function useGetCredentialRequirementsForWorker(
  qualificationNames: string[],
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const { data, ...queryResult } = useGetWorkerRequirements(
    {
      filter: {
        requiredForBooking: true,
        visibleToWorker: true,
        qualifications: qualificationNames,
        "requirement.id": [...credentialRequirementConfiguration],
      },
      include: [API_TYPES.requirements],
    },
    { ...options, enabled: !isEmpty(credentialRequirementConfiguration) }
  );

  const qualificationNameToCredentialMap = useMemo(() => {
    const map = new Map<string, WorkerRequirement[]>();

    data?.forEach((workerRequirement) => {
      const { qualifications, requirement } = workerRequirement;
      if (!isDefined(requirement?.id)) {
        return;
      }

      qualifications.forEach((qualificationName) => {
        if (credentialRequirementConfiguration.has(requirement.id)) {
          const qualificationRequirements: WorkerRequirement[] = map.get(qualificationName) ?? [];

          qualificationRequirements.push(workerRequirement);
          map.set(qualificationName, qualificationRequirements);
        }
      });
    });

    return map;
  }, [data, credentialRequirementConfiguration]);

  return { qualificationNameToCredentialMap, data, ...queryResult };
}
